import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
// import React from "react";
import "./BillList.css"; // Linked CSS file for styling
import logo from "../assets/images/logo.png"; // Adjust the path based on the file's location
import { message, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { getPaymentData } from "../utils/paymentData";
import axios from "axios";
import Swal from "sweetalert2";
import { useCurrency } from "../contexts/ValidationContext";
import { getCurrencySymbol } from "../utils/currencyHelper";
import config from "../config";
import paymentMethodMap from "../utils/paymentMethodMap"; // Import the payment method mapping

const PreviewReceipt = forwardRef(
  (
    {
      totalWithoutTaxes,
      totalCanDiscount,
      totalAfterDiscount,
      roundingAmount,
      totalWithServices,
      roundedTotalWithServices,
      deposit,
      payableAmount,
      depositMethod,
      orders,
      taxes,
      discount,
      discountType,
      discountAmount,
      tableNumber,
      takeAwayTable,
      services,
      pax,
      selectedOrders = [],
      isHidden = false,
      setPrintFirstPreview,
    },
    ref
  ) => {
    const apiBaseUrl = config.REACT_APP_API_BASE_URL;
    const [imageLogo, setImageLogo] = useState(null);
    const [restaurantName, setRestaurantName] = useState(null);
    const [restaurantAddress, setRestaurantAddress] = useState(null);
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const {} = getPaymentData();
    const [loading, setLoading] = useState(true);
    const { currency } = useCurrency();
    const symbol = getCurrencySymbol(currency);

    // Check for current logged-in user
    const getSetting = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${apiBaseUrl}/api/settings/get-settings`
        );

        if (data && data.image) {
          setImageLogo(data.image);
          setRestaurantName(data.name);
          setRestaurantAddress(data.address);
        } else {
          setImageLogo(
            "https://bucketergroup.s3.ap-southeast-1.amazonaws.com/posfolder/logo.png"
          );
          setRestaurantName("Restaurant");
          setRestaurantAddress("Address");
        }
      } catch (error) {
        // message.error("Something Went Wrong");
        Swal.fire({
          icon: "error",
          title: "Something Went Wrong",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      getSetting();
    }, []);

    useEffect(() => {
      const auth = JSON.parse(localStorage.getItem("auth"));
      if (auth) {
        const { token } = auth;
        try {
          const decoded = jwtDecode(token);
          setCurrentUser(decoded.userId); // Set the current user

          // Navigate only if the user is on an invalid page
          if (window.location.pathname === "/login") {
            navigate("/"); // Redirect to the default page only if on login
          }
        } catch (err) {
          console.error("Invalid token", err);
          localStorage.removeItem("auth");
          navigate("/login"); // Redirect to login if token is invalid
        }
      } else {
        navigate("/login"); // Redirect to login if no auth is found
      }
    }, [navigate]);
    useImperativeHandle(ref, () => ({
      printPreviewReceipt: () => {
        sendItemizedDataToBackend();
      },
    }));
    // Handle cases where orders might be undefined/null
    if (!orders || !selectedOrders.length) {
      return <div>No orders selected for receipt.</div>;
    }

    if (loading) {
      return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <Spin size="large" /> {/* Loader */}
        </div>
      );
    }

    // Safely flatten the order items, fallback to an empty array if result is null/undefined
    const orderItems = orders
      .filter((order) => selectedOrders.includes(order.oid))
      .flatMap((order) =>
        Array.isArray(order.order_items) ? order.order_items : []
      )
      .sort((a, b) => {
        const getSortValue = (item) => {
          const val = item.product?.discountAble;
          if (val === false) return 0;
          return 1; // true, null or undefined
        };

        return getSortValue(b) - getSortValue(a);
      });

    const sendItemizedDataToBackend = async () => {
      // Temporary map to consolidate items
      const consolidatedItemsMap = new Map();

      orders.forEach((order) => {
        order.order_items.forEach((item) => {
          const basePrice = item.quantity * item.product.price;

          // Calculate the total add-ons cost
          let addOnsTotal = 0;
          if (item.product.category === "Combo") {
            // For combo items: only add prices for custom options (from empty groups)
            addOnsTotal = item.product.addOns.reduce((sum, addOn) => {
              const isCustomOption = addOn.isCategoryItem ? false : (addOn.id && !addOn._id);
              return isCustomOption ? sum + addOn.price : sum;
            }, 0) * item.quantity;
          } else {
            // For regular items, add all add-on prices
            addOnsTotal = item.product.addOns.reduce((sum, addOn) => sum + addOn.price, 0) * item.quantity;
          }

          // Create a unique key based on product name, remarks, and sorted add-on names
          const addOnNames = item.product.addOns
            .map((addOn) => addOn.name)
            .sort()
            .join(",");
          const key = `${item.product.name}|${
            item.remarks || ""
          }|${addOnNames}`;

          if (consolidatedItemsMap.has(key)) {
            // Update existing item
            const existingItem = consolidatedItemsMap.get(key);
            existingItem.quantity += item.quantity;

            // Fix: Convert existingItem.total to number before adding
            existingItem.total = (
              parseFloat(existingItem.total) +
              basePrice +
              addOnsTotal
            ).toFixed(2);
          } else {
            // Add new item to the map
            consolidatedItemsMap.set(key, {
              name: item.product.name,
              discountAble: item.product.discountAble,
              quantity: item.quantity,
              total: (basePrice + addOnsTotal).toFixed(2),
              addOns: item.product.addOns.map((addOn) => ({
                name: addOn.name,
                price: addOn.price,
                group: addOn.group,
              })),
              remarks: item.remarks || "",
            });
          }
        });
      });

      // Convert map values to an array for payload
      const consolidatedOrders = Array.from(consolidatedItemsMap.values());
      const selectedPrinterId = localStorage.getItem("selectedPrinterId");
      const payload = {
        logo: imageLogo,
        resName: restaurantName,
        address: restaurantAddress,
        tableNumber,
        orders: [{ items: consolidatedOrders }],
        total: totalWithoutTaxes,
        discount: discount,
        discountAmount: discountAmount,
        discountType: discountType,
        subTotal: totalAfterDiscount,
        taxes: taxes,
        grandTotalBeforeRounding: totalWithServices,
        roundingAmount: roundingAmount,
        grandTotal: roundedTotalWithServices,
        cashierName: currentUser,
        selectedPrinterId: selectedPrinterId,
        takeAwayTable: takeAwayTable,
        services: services,
        deposits: {
          haveDeposit: Number(deposit) > 0,
          amount: deposit,
          method: paymentMethodMap[depositMethod],
          payable: payableAmount,
        },
      };

      console.log("payload", payload);

      try {
        const response = await axios.post(
          `${apiBaseUrl}/api/printprereceipt`,
          payload
        );
        if (response.data.success) {
          setPrintFirstPreview(false);
          Swal.fire({
            title: "Success",
            text: "Receipt print successfully",
            toast: true,
            icon: "success",
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: response.data.message || "Failed to print receipt",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to send itemized data",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    };



    return (
      <div>
        <style>
          {`
                .receipt-header {
                    text-align: center;
                    margin-bottom: 20px;
                    font-size: 15px;
                }

                .receipt-details {
                    margin-bottom: 20px;
                    line-height: 1.5;
                    font-size: 13px;
                }

                .receipt-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 20px;
                }

                .receipt-table th,
                .receipt-table td {
                    padding: 8px;
                    border-bottom: 1px solid #ddd;
                    font-size: 14px;
                }

                .receipt {
                    margin-top: 20px;
                }

                .receipt-row {
                    display: flex;
                    justify-content: space-between;
                    font-size: 14px;
                    font-family: Arial;
                    margin: 5px 0;
                }

                .remark-row {
                    font-size: 11px;
                    font-family: Arial;
                    margin: 5px 0;
                }
                
                .label {
                    flex: 3;
                    text-align: right;
                }

                .value {
                    flex: 1;
                    text-align: right;
                }

                // .separator {
                //     text-align: right;
                //     margin: 10px 0;
                //     color: #999;
                // }

                .separator {
                    text-align: right; 
                    border-top: 1px dashed #000; 
                    height: 1px; 
                    width: 60%;
                    margin: 10px 0;
                    margin-left: auto;
                    margin-right: 0; 
                    color: #999;
                }

                .receipt-row receipt-total{
                    display: flex;
                    justify-content: space-between;
                    font-family: Arial;
                    margin: 5px 0;

                }



                .receipt-footer {
                    text-align: center;
                    margin-top: 20px;
                    font-style: Arial;
                    color: #555;
                    font-size: 15px;
                }
            `}
        </style>
        <div className="receipt-container">
          <div className="receipt-header">
            <img
              src={imageLogo}
              alt="Restaurant Logo"
              style={{ width: "180px", height: "auto", marginBottom: "15px" }}
            />
            <p>
              <strong>Receipt</strong>
            </p>
            <p>
              <strong>{restaurantName}</strong>
            </p>
            <p style={{ fontSize: "14px" }}>
              {restaurantAddress.addressLine1}
              <br /> {restaurantAddress.addressLine2}
              <br /> {restaurantAddress.postalCodeCity}
              <br /> {restaurantAddress.stateCountry}
            </p>
          </div>

          <div className="receipt-details" style={{ textAlign: "left" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: "2px",
                rowGap: "2px",
              }}
            >
              <p>
                <strong>Table Number: </strong>
              </p>
              <p>{orders[0]?.order_tablenumber}</p>
              <p>
                <strong>Date & Time: </strong>
              </p>
              <p>{new Date().toLocaleString()}</p>
              <p>
                <strong>Cashier Name: </strong>
              </p>
              <p>{currentUser}</p>
            </div>
          </div>

          <table className="receipt-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th style={{ textAlign: "right" }}>Total ({symbol})</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(
                orderItems.reduce((acc, item) => {
                  // Create a key using product name, remarks, and sorted add-on names
                  const addOnNames = (item.product.addOns || [])
                    .map((addOn) => addOn.name)
                    .sort()
                    .join(",");
                  const key = `${item.product.name}-${
                    item.remarks || ""
                  }-${addOnNames}-$`;

                  acc[key] = acc[key] || {
                    productName: item.product.name,
                    addOns: item.product.addOns,
                    remark: item.remarks,
                    totalQuantity: 0,
                    discountAble: Boolean(item.product.discountAble !== false),
                    price: Number(item.product.price),
                    addOnTotal: 0,
                  };

                  acc[key].totalQuantity += Number(item.quantity);

                  // Calculate add-on total price
                  if (Array.isArray(item.product.addOns)) {
                    if (item.product.category === "Combo") {
                      // For combo items: only add prices for custom options (from empty groups)
                      acc[key].addOnTotal += item.product.addOns.reduce(
                        (sum, addOn) => {
                          const isCustomOption = addOn.isCategoryItem ? false : (addOn.id && !addOn._id);
                          return isCustomOption ? sum + parseFloat(addOn.price) : sum;
                        },
                        0
                      ) * Number(item.quantity);
                    } else {
                      // For regular items, add all add-on prices
                      acc[key].addOnTotal +=
                        item.product.addOns.reduce(
                          (sum, addOn) => sum + parseFloat(addOn.price),
                          0
                        ) * Number(item.quantity);
                    }
                  }
                  return acc;
                }, {})
              ).map(
                (
                  [
                    key,
                    {
                      productName,
                      remark,
                      totalQuantity,
                      discountAble,
                      price,
                      addOns,
                      addOnTotal,
                    },
                  ],
                  index
                ) => (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        {productName} {discountAble ? "" : "*"}
                        <div style={{ paddingLeft: "20px" }}>
                          {Array.isArray(addOns) && addOns.length > 0 && (
                            <ul
                              style={{
                                listStyleType: "none",
                                padding: 0,
                                margin: 0,
                              }}
                            >
                              {addOns.map((addOn, idx) => (
                                <li key={idx}>
                                  - {addOn.name} (+
                                  {(
                                    parseFloat(addOn.price) * totalQuantity
                                  ).toFixed(2)}
                                  )
                                </li>
                              ))}
                            </ul>
                          )}
                          {remark && (
                            <div style={{ fontStyle: "italic" }}>
                              {" "}
                              - Remark: {remark}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{totalQuantity}</td>
                      <td style={{ textAlign: "right" }}>
                        {(price * totalQuantity + addOnTotal).toFixed(2)}
                      </td>
                    </tr>
                  </React.Fragment>
                )
              )}

              {(() => {
                const filteredServices = services.filter(
                  (service) =>
                    service.for_take_away === takeAwayTable &&
                    service.quantity > 0
                );
                if (filteredServices.length === 0) return null;

                return filteredServices.map((service, index) => (
                  <tr key={index}>
                    <td>{service.name} **</td>
                    <td>{service.quantity}</td>
                    <td style={{ textAlign: "right" }}>
                      {service.totalPrice.toFixed(2)}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>

          <div className="receipt">
            <div className="receipt-row">
              <span className="label">Total:</span>
              <span className="value">{totalWithoutTaxes}</span>
            </div>
            <div className="receipt-row">
              {/* <span className="label"></span>  */}
              <span className="label">Discount{ discountType == '%' ? "(" + discountAmount + discountType + ")" : ""}:</span>
              <span className="value">
                {Number(discount) > 0
                  ? `- ${Number(discount).toFixed(2)}`
                  : `${Number(discount).toFixed(2)}`}
              </span>
            </div>
            <div className="receipt-row">
              <span className="label">Sub Total:</span>
              <span className="value">{totalAfterDiscount}</span>
            </div>
            {taxes &&
              taxes.length > 0 &&
              taxes
                .filter((tax) =>
                  takeAwayTable ? tax.taxValidForTakeAway : true
                )
                .map((tax, index) => (
                  <div className="receipt-row" key={index}>
                    <span className="label">
                      {tax.taxName} ({tax.taxRate}):
                    </span>
                    <span className="value">{tax.taxAmount}</span>
                  </div>
                ))}
            <div className="receipt-row">
              <span className="label">Pre-Rounding Total:</span>
              <span className="value">{totalWithServices}</span>
            </div>
            <div className="receipt-row">
              <span className="label">Rounding:</span>
              <span className="value">
                {roundingAmount < 0
                  ? `- ${Math.abs(roundingAmount).toFixed(2)}`
                  : `${Math.abs(roundingAmount).toFixed(2)}`}
              </span>
            </div>
            <div className="separator"></div>
            <div className="receipt-row receipt-total">
              <span className="label" style={{ fontSize: "18px" }}>
                Grand Total:
              </span>
              <span className="value" style={{ fontSize: "18px" }}>
                {parseFloat(roundedTotalWithServices).toFixed(2)}
              </span>
            </div>
            {Number(deposit) !== 0 && (
              <>
                <div className="receipt-row receipt-total">
                  <span className="label" style={{ fontSize: "18px" }}>
                    Deposit:
                  </span>
                  <span className="value" style={{ fontSize: "18px" }}>
                    {deposit}
                  </span>
                </div>
                <div className="receipt-row receipt-total">
                  <span className="label" style={{ fontSize: "18px" }}>
                    Payable Amount:
                  </span>
                  <span className="value" style={{ fontSize: "18px" }}>
                    {payableAmount}
                  </span>
                </div>
                <div className="receipt-row receipt-total">
                  <span className="label" style={{ fontSize: "15px" }}>
                    Deposit Method:
                  </span>
                  <span className="value" style={{ fontSize: "15px" }}>
                    {paymentMethodMap[depositMethod] || depositMethod}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="receipt-footer">
            <p>
              Items marked with * are <strong>not eligible</strong> for
              percentage discount.
            </p>
            <p>
              Service items marked with ** are <strong>only added</strong> at
              the Grand Total.
            </p>
            <p>Note: This is not the finalize receipt.</p>
          </div>
        </div>
      </div>
    );
  }
);

export default PreviewReceipt;
